import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Badge, Tabs } from 'flowbite-react';
import type { TabsRef } from 'flowbite-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Ability, DocumentationList, Loader, Select, ProjectMembers, LastActivity, Reporting, MetabaseDashboard } from '../../../components';
import { Project } from '../../../models';
import {
    RootState,
    addFavoriteProjects,
    removeFavoriteProjects,
    useAppDispatch,
    useAppSelector,
    cancelCreateChange,
} from '../../../redux';
import { loadProjects, loadProject, syncProject } from '../../../redux';
import { Projects } from '../../../services';
import {
    Alert, Icon, Button, Uploader, ProjectChecks,
    ProjectBoards, ProjectQuotes, ProjectReleases, CommitsTimeline, Changelog,
    Tooltip, ChangeModal, ChangeStandaloneSkeleton,
} from '../../../components';
import { useResetAppScrollOnMount } from '../../../hooks/useResetAppScrollOnMount';
import moment from 'moment';

type ProjectLocationState = {
    fromBoard?: boolean;
};

function ProjectView() {
    const { t }    = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const tabsRef  = useRef<TabsRef>(null);

    const [activeTab, setActiveTab]                 = useState(0);
    const [forceResetDisplay, setForceResetDisplay] = useState(false);
    const [showLinkedProjectsBar, setShowLinkedProjectsBar] = useState(false);

    const openFullSynchronisation = () => {
        setForceResetDisplay(!forceResetDisplay);
    }

    let { id, changeId, tab: tabParam } = useParams();
    const fromBoard = Boolean((location.state as ProjectLocationState | null)?.fromBoard);
    const isStandaloneChange = Boolean(changeId) && !fromBoard;
    // Avoid landing in empty space if the outlet kept a stale scrollTop.
    useResetAppScrollOnMount(isStandaloneChange);

    const { user }    = useAppSelector(state => state.auth);
    const { setting } = useAppSelector((state: RootState) => state.settings);
    const { project, projects, loadingOne, loadingOneError, sync, syncProjectId, syncError } = useAppSelector(
        (state: RootState) => state.projects
    );
    const [isTogglingProjectNotifications, setIsTogglingProjectNotifications] = useState(false);

    const projectReady = Boolean(project && id && project.matchesRouteParam(id));
    const knownProjectName = useMemo(() => {
        if (projectReady && project?.name)
            return project.name;
        if (!id || !projects?.length)
            return '';
        const cached = projects.find(
            (p: Project) => p.matchesRouteParam(id) || p._id === id || String(p.slug) === String(id)
        );
        return cached?.name || '';
    }, [projectReady, project?.name, projects, id]);

    const projectLoadFailed = Boolean(id && loadingOne === 'failed');
    const isForbiddenError = /forbidden/i.test(loadingOneError || '');
    const isNotFoundError = /not\s*found/i.test(loadingOneError || '');
    const projectAccessMessage = knownProjectName
        ? (t('projects.access_denied_named', { name: knownProjectName }) || '')
        : (isNotFoundError
            ? (t('projects.not_found') || '')
            : (isForbiddenError || !loadingOneError
                ? (t('projects.access_denied') || '')
                : loadingOneError));

    const goBackFromProject = () => {
        if (window.history.length > 1)
            navigate(-1);
        else
            navigate('/projects');
    };

    const projectAccessDenied = (
        <div className="mx-auto mt-10 w-full max-w-xl space-y-4 px-4">
            <Alert
                title={projectAccessMessage || t('projects.access_denied') || ''}
                icon="lock"
                color="failure"
            />
            <div className="flex justify-center">
                <Button
                    to="/projects"
                    title={t('projects.back_to_list') || ''}
                    icon="back"
                    color="secondary"
                    small
                />
            </div>
        </div>
    );

    const handleFavoriteProject = () => {
        if (project) {
            const findProject: string = user.favoriteProjects.find((p: string) => p === project._id)
            findProject ? dispatch(removeFavoriteProjects([findProject])) : dispatch(addFavoriteProjects([project._id]));
        }
    }

    const handleProjectNotifications = async () => {
        if (!project || isTogglingProjectNotifications)
            return;

        try {
            setIsTogglingProjectNotifications(true);
            if (project.hasSubscriber(user))
                await Projects.unsubscribeNotifications(project._id);
            else
                await Projects.subscribeNotifications(project._id);
            await dispatch(loadProject(project._id));
        } finally {
            setIsTogglingProjectNotifications(false);
        }
    };

    const linkedProjectIds = useMemo(() => {
        return new Set(
            (project?.linkedProjects || [])
                .map((p: Project | string) => (typeof p === 'string' ? p : p?._id))
                .filter(Boolean)
        );
    }, [project?.linkedProjects]);

    const linkedProjectsForNav: Project[] = useMemo(() => {
        if (!project?.linkedProjects?.length)
            return [];
        const accessibleById = new Map<string, Project>(
            (projects || []).map((p: Project) => [p._id, p])
        );
        return project.linkedProjects
            .map((linked: Project | string) => {
                const id = typeof linked === 'string' ? linked : linked?._id;
                if (!id || id === project._id)
                    return undefined;
                const fromList = accessibleById.get(id);
                if (fromList)
                    return fromList.active === false ? undefined : fromList;
                // Populated API payload: show even if not yet in the list cache
                if (typeof linked !== 'string' && linked?.name)
                    return linked.active === false ? undefined : linked;
                return undefined;
            })
            .filter((p: Project | undefined): p is Project => !!p);
    }, [project, projects]);

    useEffect(() => {
        setShowLinkedProjectsBar(false);
    }, [project?._id]);

    const sortedProjects: Project[] = useMemo(() => {
        if (!projects?.length)
            return [];

        const favorites: Project[] = [];
        const linked: Project[] = [];
        const rest: Project[] = [];

        [...projects]
            .filter((p: Project) => p.active !== false || p._id === project?._id)
            .sort((a: Project, b: Project) => (a.name > b.name ? 1 : -1))
            .forEach((p: Project) => {
                if (user.favoriteProjects.includes(p._id)) {
                    p.isFavorite = true;
                    favorites.push(p);
                    return;
                }
                if (linkedProjectIds.has(p._id)) {
                    linked.push(p);
                    return;
                }
                rest.push(p);
            });

        return favorites.concat(linked).concat(rest);
    }, [projects, user.favoriteProjects, linkedProjectIds, project?._id]);

    useEffect(() => {
        // Standalone change URLs only need the current project — skip the full list.
        if (isStandaloneChange)
            return;
        dispatch(loadProjects());
    }, [dispatch, isStandaloneChange]);

    useEffect(() => {
        if (id)
            dispatch(loadProject(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (!project || !id || !project.slug)
            return;
        if (id === project._id && project.slug !== project._id) {
            const nextPath = location.pathname.replace(
                `/projects/${id}`,
                `/projects/${project.slug}`
            );
            if (nextPath !== location.pathname)
                navigate(nextPath + location.search, { replace: true, state: location.state });
        }
    }, [project?._id, project?.slug, id, location.pathname, location.search, location.state, navigate]);

    const goToProject = (projectId: string | number) => {
        const target = sortedProjects.find((p: Project) => p._id === projectId || p.slug === projectId);
        navigate(Project.path(target || String(projectId)));
    };

    const doSync = (id: string, forceRefresh: boolean) => {
        if (id)
            dispatch(syncProject(id, forceRefresh));
    };

    const projectBoards = React.useMemo(() => {
        if (isStandaloneChange)
            return null;
        return <ProjectBoards key={project?._id} changeId={changeId} />;
    }, [ project?._id, changeId, isStandaloneChange ]);

    const isMetabaseEnabled = !!setting?.metabase?.enabled;

    // Ordered list of url slugs matching the rendered tab order.
    const hasConfiguredRepo = !!project?.hasConfiguredRepo?.();
    const hasDocumentations = !!project?.documentations?.length;
    const hiddenTabs = new Set(project?.posture?.hiddenTabs || []);

    const tabSlugByIndex: string[] = useMemo(() => [
        user.can('board:read') ? 'boards' : null,
        user.can('release:read') && hasConfiguredRepo && !hiddenTabs.has('releases') ? 'releases' : null,
        user.can('log:read') && !hiddenTabs.has('activity') ? 'activity' : null,
        user.can('commit:read') && hasConfiguredRepo && !hiddenTabs.has('commits') ? 'commits' : null,
        user.can('release:read') && hasConfiguredRepo && !hiddenTabs.has('changelog') ? 'changelog' : null,
        user.can('documentation:read') && hasDocumentations && !hiddenTabs.has('documentation') ? 'documentation' : null,
        user.can('attachment:read') && !hiddenTabs.has('attachments') ? 'attachments' : null,
        user.can('quote:read') && !hiddenTabs.has('quotes') ? 'quotes' : null,
        user.can('project:reporting:read') && !hiddenTabs.has('reporting') ? 'reporting' : null,
        isMetabaseEnabled && user.can('project:analysis:read') && !hiddenTabs.has('analysis') ? 'analysis' : null,
        user.can('project:checks:read') && !hiddenTabs.has('checks') ? 'checks' : null,
    ].filter(Boolean) as string[], [user, hasConfiguredRepo, hasDocumentations, isMetabaseEnabled, project?.posture?.hiddenTabs]);

    const tabIndexFromSlug = (slug?: string): number => {
        if (!slug) return 0;
        const idx = tabSlugByIndex.indexOf(slug);
        return idx >= 0 ? idx : 0;
    };

    // Sync Tabs component to URL param whenever project or tabParam changes.
    useEffect(() => {
        if (!project || !project.matchesRouteParam(id)) {
            return;
        }
        const idx = tabIndexFromSlug(tabParam);
        if (activeTab !== idx) {
            setActiveTab(idx);
            tabsRef.current?.setActiveTab(idx);
        }
    }, [id, tabParam, project?._id, project?.slug, tabSlugByIndex, activeTab]);

    if (isStandaloneChange && changeId) {
        // document.title is owned by ChangeModal (slug — title) once the card loads.
        if (!projectReady)
            document.title = 'Yoda';
        const headerLabel = projectReady
            ? project!.name
            : (projectLoadFailed
                ? (knownProjectName || t('projects.access_denied') || '')
                : (t('common.loading') || '…'));
        return (
            <Ability can="project:read" redirect="/">
                <div className="ChangeStandaloneView flex flex-col px-4 py-3 sm:px-6 lg:px-8">
                    <div className="mb-3 flex shrink-0 items-center gap-2">
                        <Button
                            onClick={() => {
                                if (projectReady)
                                    navigate(Project.path(project!));
                                else
                                    goBackFromProject();
                            }}
                            title={t('common.back') || ''}
                            icon="back"
                            color="navigateBack"
                            small
                        />
                        <span className="truncate text-sm text-gray-500 dark:text-gray-400">
                            {headerLabel}
                        </span>
                    </div>
                    <div className="flex flex-col">
                        {projectReady ? (
                            <ChangeModal
                                project={project!}
                                changeId={changeId}
                                show
                                standalone
                                showProjectName
                                onClose={() => {
                                    dispatch(cancelCreateChange());
                                    navigate(Project.path(project!));
                                }}
                                onRemove={() => {
                                    // Soft-delete is handled inside ChangeModal.
                                    // Do not cancelCreateChange here: it nulls `change` and
                                    // the standalone view would fall back to the skeleton.
                                }}
                            />
                        ) : projectLoadFailed ? (
                            projectAccessDenied
                        ) : (
                            <ChangeStandaloneSkeleton />
                        )}
                    </div>
                </div>
            </Ability>
        );
    }

    if (projectLoadFailed)
        return (
            <Ability can="project:read" redirect="/">
                <div className="ProjectView">
                    <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-3 sm:px-6 lg:px-8">
                        <div className="flex items-center gap-2">
                            <Button
                                onClick={goBackFromProject}
                                title={t('common.back') || ''}
                                icon="back"
                                color="navigateBack"
                                small
                            />
                            <h2 className="truncate text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                {knownProjectName || t('projects.project')}
                            </h2>
                        </div>
                    </header>
                    {projectAccessDenied}
                </div>
            </Ability>
        );

    if (!projectReady)
        return <Loader />;

    const onTabChange = (index: number) => {
        if (activeTab !== index)
            setActiveTab(index);
        const slug = tabSlugByIndex[index];
        const projectKey = Project.navKey(project);
        if (!projectKey) return;
        const targetPath = (slug && slug !== 'boards')
            ? Project.path(project, 'tab', slug)
            : Project.path(project);

        if (location.pathname !== targetPath)
            navigate(targetPath);
    };

    const isActiveTab = (tab: string): boolean => {
        const slug = tab === 'release' ? 'releases' : tab;
        return activeTab === tabSlugByIndex.indexOf(slug);
    };

    const theme = {
        "base": "flex flex-col gap-2",
        "tablist": {
            "base": "grid grid-cols-2 sm:grid-cols-4 md:flex text-center",
            "styles": {
                "underline": "flex-wrap -mb-px border-b border-gray-200 dark:border-gray-700",
            },
            "tabitem": {
                "base": "flex items-center justify-center p-4 rounded-t-lg text-sm font-medium first:ml-0 disabled:cursor-not-allowed disabled:text-gray-400 disabled:dark:text-gray-500 !focus:ring-0 focus:outline-none",
                "styles": {
                    "underline": {
                        "base": "rounded-t-lg",
                        "active": {
                            "on": "text-cyan-600 rounded-t-lg border-b-2 border-cyan-600 active dark:text-cyan-500 dark:border-cyan-500",
                            "off": "border-b-2 border-transparent text-gray-500 hover:border-gray-300 dark:border-gray-600 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                        }
                    },
                },
                "icon": "mr-2 h-5 w-5"
            }
        },
        "tabitemcontainer": {
            "base": "",
            "styles": {
                "underline": "",
            }
        },
        "tabpanel": "py-3"
    };

    // When a card is open from the board URL, ChangeModal owns document.title.
    if (!changeId)
        document.title = `Yoda - ${t('projects.project')} ${project.name}`;

    return (
        <Ability can="project:read" redirect="/">
            <div
                className="ProjectView"
                style={{
                    ['--yoda-board-vh-offset' as any]:
                        linkedProjectsForNav.length > 1 && showLinkedProjectsBar ? '17rem' : '14rem'
                }}
            >
                <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-0 sm:px-6 lg:px-8 pb-2 lg:pb-0">
                    <div className="mx-auto lg:flex lg:itemProjects-center lg:justify-between items-center">
                        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0">
                            <h2 className="flex items-center text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                <Avatar img={project.getAvatarUrl(45)} className="inline" size="md" />
                                <Select
                                    onChange={goToProject}
                                    placeholder={project?.name}
                                    options={sortedProjects.map((project: Project) => ({
                                        value: Project.navKey(project),
                                        label: project.name,
                                        className: 'text-xl font-bold',
                                        leftContent: <Avatar img={project.getAvatarUrl()} className="inline ml-2" />,
                                        rightContent: project.isFavorite ? <Icon type="fullStar" color="yellow-400" className="mr-2"/> : null
                                    }))}
                                    inline
                                    className=""
                                    optionsWidth={'w-[20rem]'}
                                    optionsHeight={'max-h-96'}
                                    selectedClassName="text-2xl font-bold text-gray-900 dark:text-gray-100"
                                    optionsClassName="py-4 pl-14"
                                />
                                {project.active === false && (
                                    <Badge color="gray" className="ml-3 self-center">{t('projects.archived')}</Badge>
                                )}
                            </h2>
                            {linkedProjectsForNav.length === 1 && (
                                <button
                                    type="button"
                                    onClick={() => goToProject(Project.navKey(linkedProjectsForNav[0]))}
                                    className="inline-flex items-center gap-1.5 self-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-gray-100 sm:ml-3"
                                    title={linkedProjectsForNav[0].name}
                                >
                                    <Avatar img={linkedProjectsForNav[0].getAvatarUrl(24)} className="!h-5 !w-5" size="xs" />
                                    <span className="max-w-[8rem] truncate font-medium">{linkedProjectsForNav[0].name}</span>
                                </button>
                            )}
                            {linkedProjectsForNav.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setShowLinkedProjectsBar((open) => !open)}
                                    className={`inline-flex items-center gap-1.5 self-center rounded-full border px-2.5 py-1 text-sm font-medium sm:ml-3 ${
                                        showLinkedProjectsBar
                                            ? 'border-gray-700 bg-gray-800 text-white'
                                            : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-gray-100'
                                    }`}
                                    title={t('projects.linked_projects') || ''}
                                    aria-expanded={showLinkedProjectsBar}
                                >
                                    <span>{t('projects.linked_projects')}</span>
                                    <span className={`rounded-full px-1.5 text-xs ${
                                        showLinkedProjectsBar
                                            ? 'bg-gray-600 text-gray-100'
                                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                                    }`}>
                                        {linkedProjectsForNav.length}
                                    </span>
                                </button>
                            )}
                            <Ability can="project:members:read">
                                <div className="sm:ml-4 sm:self-center">
                                    <ProjectMembers />
                                </div>
                            </Ability>
                        </div>
                            {/* TODO : mettre un button avec "icone" + text comme sur les bouttons */}
                        <div className="flex flex-wrap items-center justify-end gap-2 lg:ml-4 mt-2 lg:mt-0 overflow-x-auto">
                            <div className="flex items-center gap-2">
                                <Tooltip content={t('projects.help.favorite') || ''} light>
                                    <Button
                                        onClick={handleFavoriteProject}
                                        title=""
                                        type='button'
                                        color='warning'
                                        className="shrink-0"
                                        iconColor='white-500'
                                        icon={user.favoriteProjects.find((p: string) => p === project._id) ? "fullStar" : "emptyStar"}
                                    />
                                </Tooltip>
                                <Tooltip content={t('projects.help.subscribe') || ''} light>
                                    <Button
                                        onClick={handleProjectNotifications}
                                        title=""
                                        type='button'
                                        color={project.hasSubscriber(user) ? 'secondary' : 'warning'}
                                        className="shrink-0"
                                        iconColor='white-500'
                                        loading={isTogglingProjectNotifications}
                                        icon={project.hasSubscriber(user) ? "reminder" : "reminderOff"}
                                    />
                                </Tooltip>
                                {project?.hasConfiguredRepo() && (
                                    <Tooltip content={t('projects.help.open_gitlab') || ''} light>
                                        <Button
                                            title=""
                                            icon="gitlab"
                                            color="navigate"
                                            className="shrink-0"
                                            to={ project.repository.url }
                                            target="_blank"
                                        />
                                    </Tooltip>
                                )}
                            </div>
                            {project?.hasConfiguredRepo() && user?.can('project:sync') && (
                            <span className="flex flex-row relative sm:mr-3">
                                <Tooltip content={t('projects.help.sync') || ''} light>
                                    <Button
                                        onClick={() => doSync(project._id, /*forceRefresh*/false)}
                                        title={t('projects.sync')}
                                        icon="sync"
                                        loading={(syncProjectId === project._id) && (sync === 'pending')}
                                        color="primary"
                                        className={`w-full rounded-e-none ${forceResetDisplay ? 'hidden' : 'flex'}`}
                                    />
                                </Tooltip>
                                <Tooltip content={t('projects.help.sync_full') || ''} light>
                                    <Button
                                        onClick={() => doSync(project._id, /*forceRefresh*/true)}
                                        title={t('projects.sync_full')}
                                        icon="sync"
                                        loading={(syncProjectId === project._id) && (sync === 'pending')}
                                        color="default"
                                        className={`w-full rounded-e-none ${forceResetDisplay ? 'flex' : 'hidden'}`}
                                    />
                                </Tooltip>
                                <Button
                                    className={`h-9 rounded-s-none border-l`}
                                    color='primary'
                                    title=''
                                    icon={`${forceResetDisplay ? 'close' : 'dots'}`}
                                    onClick={() => openFullSynchronisation()}
                                />
                            </span>
                            )}
                            <Ability can="project:edit">
                                <Tooltip content={t('common.edit') || ''} light>
                                    <Button
                                        to={Project.editPath(project)}
                                        title=""
                                        icon="settings"
                                        color="default"
                                        className="shrink-0"
                                    />
                                </Tooltip>
                            </Ability>
                        </div>
                    </div>
                </header>
                {linkedProjectsForNav.length > 1 && showLinkedProjectsBar && (
                    <div className="bg-gray-800 px-4 sm:px-6 lg:px-8 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                            {linkedProjectsForNav.map((linked: Project) => (
                                <button
                                    key={linked._id}
                                    type="button"
                                    onClick={() => goToProject(Project.navKey(linked))}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-600 bg-gray-700 px-2.5 py-1 text-sm text-gray-100 hover:bg-gray-600 hover:text-white"
                                    title={linked.name}
                                >
                                    <Avatar img={linked.getAvatarUrl(24)} className="!h-5 !w-5" size="xs" />
                                    <span className="max-w-[12rem] truncate font-medium">{linked.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {(syncProjectId === project?._id) && (sync !== 'idle') && (
                <div className="flex-1">
                    {sync === 'pending' && (
                        <Alert title={t('projects.syncing')} color="info" icon="loading" />
                    )}
                    {sync === 'succeeded' && (
                        <Alert title={t('projects.sync_succeeded')} color="success" icon="check" />
                    )}
                    {syncError && (
                        <Alert title={t('projects.sync_failed') + ' : ' + syncError} color="failure" icon="warning" />
                    )}
                </div>
                )}
                <main className="mx-3 mt-1">
                    {project && project.matchesRouteParam(id) && (
                        <Tabs ref={tabsRef} style="underline" onActiveTabChange={onTabChange} theme={theme} >
                            {user?.can('board:read') && (
                            <Tabs.Item icon={() => <Icon type="board" className="mr-1.5 outline-none" color="gray-800"/>} title={t('projects.boards')}>
                                <div className="-mt-3 -mb-4">
                                    {isActiveTab('boards') && projectBoards}
                                </div>
                            </Tabs.Item>
                            )}
                            {user?.can('release:read') && project.hasConfiguredRepo() && !hiddenTabs.has('releases') ? (
                            <Tabs.Item icon={() => <Icon type="release" className="mr-1.5 outline-none" />} title={t('projects.releases')}>
                                <div className="-mt-3 flex-1 shrink">
                                    {isActiveTab('release') && (
                                        <ProjectReleases project={project} />
                                    )}
                                </div>
                            </Tabs.Item>
                            ) : ''}
                            {user?.can('log:read') && !hiddenTabs.has('activity') && (
                            <Tabs.Item icon={() => <Icon type="activity" className="mr-1.5 outline-none" />} title={t('projects.activity')}>
                                <div className="flex-1 shrink">
                                    {isActiveTab('activity') && (
                                        <LastActivity title={t('projects.activity') || ''} project={project?._id} limit={100} />
                                    )}
                                </div>
                            </Tabs.Item>
                            )}
                            {user?.can('commit:read') && project.hasConfiguredRepo() && !hiddenTabs.has('commits') ? (
                            <Tabs.Item icon={() => <Icon type="commit" className="mr-1.5 outline-none" />} title={t('projects.commits')}>
                                <div className="flex-1 shrink">
                                    {isActiveTab('commits') && (
                                        <CommitsTimeline project={project} />
                                    )}
                                </div>
                            </Tabs.Item>
                            ) : ''}
                            {user?.can('release:read') && project.hasConfiguredRepo() && !hiddenTabs.has('changelog') ? (
                            <Tabs.Item icon={() => <Icon type="changelog" className="mr-1.5 outline-none" />} title={t('projects.changelog')}>
                                <div className="flex-1 shrink">
                                    {isActiveTab('changelog') && (
                                        <Changelog project={project} />
                                    )}
                                </div>
                            </Tabs.Item>
                            ) : ''}
                            {user?.can('documentation:read') && project.documentations.length && !hiddenTabs.has('documentation') && (
                            <Tabs.Item icon={() => <Icon type="comment" className="mr-1.5 outline-none" />} title={t('projects.documentation')}>
                                <div className="flex-1 shrink">
                                    {isActiveTab('documentation') && (
                                        <DocumentationList projectId={project._id}/>
                                    )}
                                </div>
                            </Tabs.Item>
                            )}
                            {user?.can('attachment:read') && !hiddenTabs.has('attachments') && (
                            <Tabs.Item
                                icon={() => <Icon type="file" className="mr-1.5 outline-none" />}
                                title={t('projects.attachments') + (project?.nbAttachments > 0 ? ' (' + project.nbAttachments +')' : '')}
                            >
                                <div className="flex-1 shrink">
                                    {isActiveTab('attachments') && (
                                        <Uploader projectId={project?._id} showChangeSlug/>
                                    )}
                                </div>
                            </Tabs.Item>
                            )}
                            {user?.can('quote:read') && !hiddenTabs.has('quotes') && (
                            <Tabs.Item
                                icon={() => <Icon type="law" className="mr-1.5 outline-none" />}
                                title={t('projects.quotes')}
                            >
                                <div className="flex-1 shrink">
                                    {isActiveTab('quotes') && (
                                        <ProjectQuotes project={project} />
                                    )}
                                </div>
                            </Tabs.Item>
                            )}
                            {user?.can('project:reporting:read') && !hiddenTabs.has('reporting') && (
                            <Tabs.Item
                                icon={() => <Icon type="report" className="mr-1.5 outline-none" />}
                                title={t('projects.reporting')}
                            >
                                <div className="flex-1 shrink">
                                    {isActiveTab('reporting') && (
                                        <Reporting
                                            project={project}
                                            client={project.clients?.length === 1 ? project.clients[0] : undefined}
                                            from={moment().startOf('month')}
                                            to={moment().endOf('day')}
                                            tabs={['dashboard', 'tracks']}
                                        />
                                    )}
                                </div>
                            </Tabs.Item>
                            )}
                            {isMetabaseEnabled && user?.can('project:analysis:read') && !hiddenTabs.has('analysis') && (
                            <Tabs.Item
                                icon={() => <Icon type="chart" className="mr-1.5 outline-none" />}
                                title={t('projects.analysis')}
                            >
                                <div className="flex-1 shrink">
                                    {isActiveTab('analysis') && (
                                        <MetabaseDashboard project={project} />
                                    )}
                                </div>
                            </Tabs.Item>
                            )}
                            {user?.can('project:checks:read') && !hiddenTabs.has('checks') && (
                            <Tabs.Item
                                icon={() => <Icon type="check" className="mr-1.5 outline-none" />}
                                title={t('projects.checks')}
                            >
                                <div className="flex-1 shrink">
                                    {isActiveTab('checks') && (
                                        <ProjectChecks project={project} />
                                    )}
                                </div>
                            </Tabs.Item>
                            )}
                        </Tabs>
                    )}
                </main>
            </div>
        </Ability>
    );
}

export default ProjectView;
